import React from 'react'
import { AI_TOOLS } from '../common'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { useTextScramble } from '../hooks/useTextScramble'

const AISection = () => {
	const [ref, isVisible] = useScrollAnimation(0.1)
	const scrambled = useTextScramble('AI', isVisible, 700)

	return (
		<section id='ai' className='py-24 px-6 lg:px-12' ref={ref}>
			<div className='max-w-6xl mx-auto'>
				{/* Section header */}
				<div
					className='mb-16 transition-all duration-700'
					style={{
						opacity: isVisible ? 1 : 0,
						transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
					}}>
					<p className='text-blue-400 font-mono text-xs tracking-[0.25em] uppercase mb-3'>
						Artificial Intelligence
					</p>
					<h2 className='text-4xl lg:text-6xl font-bold text-white'>
						Working with{' '}
						<span className='bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent font-mono'>
							{scrambled}
						</span>
					</h2>
					<div className='mt-4 w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full' />
					<p className='mt-6 text-gray-400 text-base max-w-2xl leading-relaxed'>
						In the age of AI, I actively integrate large language models into my
						development workflow — from accelerating code reviews to architecting
						complex systems and generating documentation at scale.
					</p>
				</div>

				{/* AI tool cards */}
				<div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
					{AI_TOOLS.map((tool, i) => (
						<div
							key={tool.name}
							className={`relative p-6 rounded-2xl border ${tool.border} bg-gradient-to-br ${tool.gradient} transition-all duration-700 hover:scale-[1.02] group`}
							style={{
								opacity: isVisible ? 1 : 0,
								transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
								transitionDelay: `${i * 120}ms`,
								background: `linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))`,
							}}>
							{/* Subtle glow on hover */}
							<div
								className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
								style={{
									background: `radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08), transparent 70%)`,
								}}
							/>

							<div className='flex items-start gap-4 mb-4'>
								{/* Logo placeholder with initial */}
								<div
									className={`w-11 h-11 rounded-xl ${tool.initialBg} flex items-center justify-center flex-shrink-0 shadow-lg`}>
									<span className='text-white font-bold text-lg'>
										{tool.initial}
									</span>
								</div>
								<div>
									<h3 className='text-white font-bold text-lg leading-tight'>
										{tool.name}
									</h3>
									<span className='text-gray-500 text-xs'>{tool.company}</span>
								</div>
							</div>

							<p className='text-gray-400 text-sm leading-relaxed'>
								{tool.desc}
							</p>

							<div className='mt-5'>
								<span
									className={`text-xs px-2.5 py-1 rounded-md border ${tool.badge} font-medium`}>
									Daily Driver
								</span>
							</div>
						</div>
					))}
				</div>

				{/* Bottom note */}
				<div
					className='mt-10 p-5 rounded-2xl border border-white/6 flex items-start gap-4 transition-all duration-700'
					style={{
						background: 'rgba(255,255,255,0.02)',
						opacity: isVisible ? 1 : 0,
						transitionDelay: '400ms',
					}}>
					<div className='w-9 h-9 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0 mt-0.5'>
						<i className='fas fa-lightbulb text-blue-400 text-sm' />
					</div>
					<p className='text-gray-400 text-sm leading-relaxed'>
						I use AI as a{' '}
						<span className='text-white font-medium'>force multiplier</span> —
						not a replacement for engineering judgment. Every AI-assisted
						decision goes through rigorous code review, testing and
						architectural validation.
					</p>
				</div>
			</div>
		</section>
	)
}

export default AISection
